import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { CommonRootState } from '@common/types/common-root-state-type.d';
import {
  selectFeatureAdoptionData,
  selectNodeTypeDistributionData,
  selectNodeTypeDistributionState,
  selectFeatureAdoptionState
} from '../BotUsageDashboardSelectors';
import { Loader2 } from 'lucide-react';
import { WebRootState } from '@/website/store/web-store';

interface NodeTypeItem {
  name: string;
  value: number;
  color: string;
}

interface FeatureAdoptionItem {
  name: string;
  value: number;
}

// Remove default mock data

/**
 * NodeUsageTab Component
 * 
 * Displays snapshot stats related to node types and feature adoption
 */
export const NodeUsageTab: React.FC = () => {
  const { t } = useTranslation(namespace);
  // Get data from Redux store using updated selectors
  const nodeTypeDistributionState = useSelector((state: WebRootState) =>
    selectNodeTypeDistributionState(state)
  );
  const nodeTypeDistributionData = useSelector((state: WebRootState) => 
    selectNodeTypeDistributionData(state)
  );
  
  const featureAdoptionState = useSelector((state: WebRootState) => 
    selectFeatureAdoptionState(state)
  );
  const featureAdoptionData = useSelector((state: WebRootState) => 
    selectFeatureAdoptionData(state)
  );

  // Extract loading states
  const nodeTypeLoading = nodeTypeDistributionState.loading;
  const featureAdoptionLoading = featureAdoptionState.loading;

  // Complexity distribution is not yet available from the API
  // We'll show a loading state for this section

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('botUsageDashboard.nodeUsage.nodeTypeDistribution')}</CardTitle>
            <CardDescription>{t('botUsageDashboard.nodeUsage.nodeTypeDistributionDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {nodeTypeLoading || !nodeTypeDistributionData || nodeTypeDistributionData.length === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={nodeTypeDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {nodeTypeDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} ${t('botUsageDashboard.nodeUsage.uses')}`, t('botUsageDashboard.nodeUsage.count')]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('botUsageDashboard.nodeUsage.featureAdoption')}</CardTitle>
            <CardDescription>{t('botUsageDashboard.nodeUsage.featureAdoptionDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {featureAdoptionLoading || !featureAdoptionData || featureAdoptionData.length === 0 ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={featureAdoptionData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                    <YAxis type="category" dataKey="name" />
                    <Tooltip formatter={(value) => [`${value}%`, t('botUsageDashboard.nodeUsage.adoption')]} />
                    <Bar dataKey="value" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('botUsageDashboard.nodeUsage.complexWorkflowAnalysis')}</CardTitle>
          <CardDescription>{t('botUsageDashboard.nodeUsage.complexWorkflowDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500">{t('botUsageDashboard.nodeUsage.dataNotAvailable')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}; 